import React, { useState, useEffect } from "react"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { useHistory } from "react-router-dom"
import { ToastContainer, toast } from "react-toastify"
import Dropzone from "react-dropzone"
import { URLS } from "../../Url"
import axios from "axios"
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  FormGroup,
} from "reactstrap"

const EditSalesman = () => {
  const history = useHistory()

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    address: "",
    id: "",
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [thumbnailImage, setThumbnailImage] = useState(null)
  const [existingImage, setExistingImage] = useState("")

  const token = JSON.parse(localStorage.getItem("authUser"))?.token

  useEffect(() => {
    let salesmanDataStr = sessionStorage.getItem("SalesmanData")
    if (salesmanDataStr) {
      let salesmanData = JSON.parse(salesmanDataStr)
      setFormData({
        name: salesmanData.name || "",
        phone: salesmanData.phone || "",
        email: salesmanData.email || "",
        password: salesmanData.password || "", // Usually shouldn't show hash, but keeping it empty isn't great. If backend wants raw, maybe leave it empty for no update.
        address: salesmanData.address || "",
        id: salesmanData._id || "",
      })
      if (salesmanData.image) {
        setExistingImage(URLS.Base + salesmanData.image)
      }
    } else {
      history.push("/SalesmanList")
    }
  }, [history])

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }))
    }
  }

  const handleImageUpload = acceptedFiles => {
    if (acceptedFiles.length === 0) return

    const file = acceptedFiles[0]
    setThumbnailImage({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    })
  }

  const removeImage = e => {
    if (e) e.stopPropagation()
    if (thumbnailImage) {
      URL.revokeObjectURL(thumbnailImage.preview)
      setThumbnailImage(null)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name?.trim()) newErrors.name = "Name is required"
    if (!formData.phone?.trim()) newErrors.phone = "Phone is required"
    if (!formData.email?.trim()) newErrors.email = "Email is required"
    if (!formData.address?.trim()) newErrors.address = "Address is required"
    // Assuming password is not strictly required on edit if unchanged
    // but the prompt API payload includes it.
    if (!formData.password?.trim()) newErrors.password = "Password is required"

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error("Please fix the errors in the form")
      return
    }

    setIsSubmitting(true)

    try {
      const formDataToSend = new FormData()
      formDataToSend.append("name", formData.name)
      formDataToSend.append("phone", formData.phone)
      formDataToSend.append("email", formData.email)
      formDataToSend.append("password", formData.password)
      formDataToSend.append("address", formData.address)
      formDataToSend.append("id", formData.id)

      if (thumbnailImage?.file) {
        formDataToSend.append("image", thumbnailImage.file)
      }

      const response = await axios.put(URLS.EditSalesman, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })

      if (response.data.success) {
        sessionStorage.setItem("tost", response.data.message || "Updated successfully")
        history.push("/SalesmanList")
      } else {
        toast.error(response.data.message || "Failed to edit salesman")
      }
    } catch (error) {
      console.error("Error editing salesman:", error)
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to edit salesman. Please try again."
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Manage Salesman" breadcrumbItem="Edit Salesman" />
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <div className="mb-2">
                    <h5 className="mb-1 text-primary">Salesman Information</h5>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label>Name *</Label>
                          <Input
                            type="text"
                            name="name"
                            value={formData.name || ""}
                            onChange={handleInputChange}
                            invalid={!!errors.name}
                            placeholder="Enter name"
                          />
                          <FormFeedback>{errors.name}</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Phone *</Label>
                          <Input
                            type="text"
                            name="phone"
                            value={formData.phone || ""}
                            onChange={handleInputChange}
                            invalid={!!errors.phone}
                            placeholder="Enter phone"
                          />
                          <FormFeedback>{errors.phone}</FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label>Email *</Label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email || ""}
                            onChange={handleInputChange}
                            invalid={!!errors.email}
                            placeholder="Enter email"
                          />
                          <FormFeedback>{errors.email}</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Password *</Label>
                          <Input
                            type="text"
                            name="password"
                            value={formData.password || ""}
                            onChange={handleInputChange}
                            invalid={!!errors.password}
                            placeholder="Enter new password or repeat old"
                          />
                          <FormFeedback>{errors.password}</FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="12">
                        <FormGroup>
                          <Label>Address *</Label>
                          <Input
                            type="textarea"
                            name="address"
                            value={formData.address || ""}
                            onChange={handleInputChange}
                            invalid={!!errors.address}
                            placeholder="Enter address"
                            rows="3"
                          />
                          <FormFeedback>{errors.address}</FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                  <div className="mb-4">
                    <h5 className="mb-1 text-primary">Profile Image</h5>
                    <Row>
                      <Col md="12">
                        <Dropzone
                          onDrop={handleImageUpload}
                          accept="image/*"
                          maxSize={5242880}
                          multiple={false}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div
                              {...getRootProps()}
                              className="dropzone border rounded p-4 text-center"
                              style={{
                                borderStyle: "dashed",
                                cursor: "pointer",
                                backgroundColor: "#f8f9fa",
                              }}
                            >
                              <input {...getInputProps()} />
                              <div className="mb-1">
                                <i className="display-4 text-muted bx bxs-cloud-upload" />
                              </div>
                              <h5>
                                Drag and drop an image here, or click to select
                              </h5>
                              <p className="text-muted">
                                Maximum 1 image (Max size: 5MB)
                              </p>
                            </div>
                          )}
                        </Dropzone>
                      </Col>
                    </Row>
                    {thumbnailImage ? (
                      <Row className="mt-3">
                        <Col md="12">
                          <h6 className="mb-2">Selected Image:</h6>
                          <div className="d-flex flex-wrap">
                            <div
                              className="position-relative m-2"
                              style={{ width: "120px" }}
                            >
                              <img
                                src={thumbnailImage.preview}
                                alt="Preview"
                                className="img-thumbnail"
                                style={{
                                  height: "120px",
                                  objectFit: "cover",
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                style={{ transform: "translate(50%, -50%)" }}
                                onClick={removeImage}
                              >
                                <i className="bx bx-x" />
                              </button>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    ) : existingImage ? (
                      <Row className="mt-3">
                        <Col md="12">
                          <h6 className="mb-2">Current Image:</h6>
                          <div className="d-flex flex-wrap">
                            <div
                              className="position-relative m-2"
                              style={{ width: "120px" }}
                            >
                              <img
                                src={existingImage}
                                alt="Current Preview"
                                className="img-thumbnail"
                                style={{
                                  height: "120px",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>
                    ) : null}
                  </div>

                  <div className="d-flex justify-content-end mt-4">
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting}
                      className="px-5"
                    >
                      {isSubmitting ? "Updating..." : "Update Salesman"}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <ToastContainer />
      </Container>
    </div>
  )
}

export default EditSalesman
